import { FormProvider, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { NodeUI } from 'modules/workflows/components/Node/types'
import {
  NodeSidepanelSchema,
  nodeSidepanelSchema,
} from 'modules/workflows/components/Sidepanel/schemas/nodeSidepanelSchema'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'

interface Props {
  selectedNode: NodeUI
}
export const SidePanelFormProvider = ({
  selectedNode,
  children,
}: React.PropsWithChildren<Props>) => {
  const { setSidepanelDirty, handlerForms } = useWorkflowContext()

  const initialHandlerName = String(selectedNode.data.handler_name)

  const getFormValues = () => {
    const handlerForm = handlerForms[initialHandlerName]

    if (selectedNode.type === 'task' || selectedNode.type === 'approvals') {
      return {
        nodeType: selectedNode.type,
        title: selectedNode.data.title,
        description: selectedNode.data.description,
        nodeData: {
          payload: handlerForm?.initializer?.(selectedNode.data.payload),
          handlerType: initialHandlerName,
        },
      }
    }
    return {
      //the other types are not supported atm, so the form is empty
      nodeType: selectedNode.type,
      title: selectedNode.data.title,
      description: selectedNode.data.description,
      nodeData: {},
    }
  }

  const form = useForm<NodeSidepanelSchema>({
    mode: 'onSubmit',
    resolver: async (values, context, options) => {
      const handlerName = values.nodeData.handlerType
      const handlerForm = handlerForms[handlerName]
      const handlerSchema = handlerForm?.schema ?? nodeSidepanelSchema //use generic schema by default, which shows error message when handler type is not selected

      const createResolver = zodResolver(handlerSchema)

      return createResolver(values, context, options)
    },
    defaultValues: getFormValues(),
  })

  const { isDirty } = form.formState

  useUnsafeEffect(() => {
    setSidepanelDirty(isDirty)
  }, [isDirty, setSidepanelDirty])

  return <FormProvider {...form}>{children}</FormProvider>
}
